"use client"

// Armador de pizza por zonas para el cliente final (microsite y kiosko).
//
// 🔴 NO TIENE LÓGICA DE PRECIO PROPIA. Todo sale de lib/menu/zonedBuilder +
// zonedPricing, que es lo mismo que corre el servidor al cotizar. Sumar acá por
// separado crearía una segunda verdad de precio: la pantalla diría $3.00 y la caja
// cobraría $2.99 (cuatro cuartos de $2.99 colapsan a pizza entera, §6.2).
//
// ── v2 (2026-08-02) ─────────────────────────────────────────────────────────
// La v1 renderizaba TODAS las opciones en una lista plana, ignorando los grupos.
// De ahí salían los dos defectos que reportó Samuel probando en redy-test:
//   1. se podían marcar "Sin queso", "Normal" y "Doble queso" A LA VEZ — la
//      selección única no la imponía nadie, y eso llega así a la cocina;
//   2. no había ningún orden: tamaño, masa y toppings mezclados sin jerarquía.
// Los dos tienen la misma causa y el mismo arreglo: el armador ahora es CONSCIENTE
// DE LOS GRUPOS y los pinta como secciones, en orden de decisión.
//
// Patrones tomados de Domino's y Pizza Hut (capturas de Samuel):
//   - secciones con encabezado, en orden fijo;
//   - radio de verdad donde solo cabe una opción ("Queso" vs "Sin queso");
//   - la INTENSIDAD vive dentro de la tarjeta elegida (Normal / Doble), no como
//     una opción hermana que compite con ella;
//   - barra fija abajo con precio y "Agregar".
//
// Y la decisión de zona vive DENTRO de cada ingrediente, no como un modo global:
// así no hay que subir a cambiar de lado y bajar a elegir el topping.
//
// Diseño: docs/superpowers/specs/2026-07-29-pizza-zoned-modifiers-design.md (§8)

import { useEffect, useMemo, useReducer, useState } from "react"
import { useLowStock } from "@/components/menu/LowStockContext"
import { LowStockBadge } from "@/components/menu/LowStockBadge"
import {
  countZonedUnits,
  effectiveZonedMin,
  initZonedBuild,
  priceZonedBuild,
  sumZonedCents,
  validateZonedGroup,
  zonedBuildReducer,
  type PricedZonedSelection,
  type ZonedBuildAction,
  type ZonedBuildState,
  type ZonedGroupRule,
} from "@/lib/menu/zonedBuilder"
import {
  resolveZonedUnitPriceCents,
  type Zone,
  type ZoneLayout,
  type ZonedPricingMode,
  type ZonedSelection,
} from "@/lib/menu/zonedPricing"
import { resolveBaseCardPrice, type SizePriceCell } from "@/lib/menu/zonedQuote"
import {
  escalaDeTamano, masaDe, nivelQuesoDe, salsaDe,
  type Masa, type NivelQueso, type Salsa,
} from "@/lib/menu/pizzaBase"
import { effectiveUnitPrice, type PricingConfig } from "@/lib/utils/pricing"
import { seedDefaultOptionIds, pickSizeOptionId } from "@/lib/menu/zonedDefaults"
import { PizzaCanvas, IngredientGlyph, type PizzaTopping } from "@/components/menu/PizzaCanvas"

/** Solo mitades en el público: cuatro partes en un teléfono son botones diminutos
 *  y una decisión que casi nadie toma. Los cuartos siguen vivos en el motor. */
const ZONES: Zone[] = ["whole", "left", "right"]
const ZONE_SHORT: Record<Zone, string> = {
  whole: "Toda", left: "Izquierda", right: "Derecha",
  q1: "Sup. izq.", q2: "Sup. der.", q3: "Inf. der.", q4: "Inf. izq.",
}

export type ZonedOption = {
  id: string
  name: string
  /** En inglés, solo si difiere del canónico. Lo pone quien arma los grupos
   *  (el POS, desde `modifier_options.translations`); el kiosko no lo necesita
   *  porque ya pinta sus propios rótulos. Viaja a la pantalla del cliente en
   *  `labelEn`/`nameEn`, mismo contrato que el carrito y el armador genérico. */
  nameEn?: string
  color: string
  /** `modifier_options.price_delta`: el precio PLANO, sin tamaño. Es la caída de
   *  D10 cuando la rejilla no tiene celda para el tamaño elegido — el precio que se
   *  muestra sale de `sizeGrid`, no de acá. */
  basePrice: number
  isDefault: boolean
  /** 86: se muestra apagado y no se puede colocar. NO se siembra como default
   *  aunque lo sea — ver lib/menu/zonedDefaults. */
  soldOut?: boolean
}

export type ZonedGroup = {
  id: string
  name: string
  /** D8: false para masa y tamaño — la cocina no puede partirlos. */
  zonable: boolean
  /** `max_selections === 1`: elegir una DESELECCIONA a sus hermanas. */
  single: boolean
  /** El grupo que gobierna la rejilla precio×tamaño. */
  isSize: boolean
  /** `modifier_groups.is_required`. Con esto el armador exige el mínimo ANTES de
   *  "Agregar" — sin ello el cliente armaba una pizza que el servidor rechazaba
   *  con 422 recién al cobrar. */
  required: boolean
  /** `min_selections` (0 si no aplica). El mínimo cuenta POR LÍNEA (D5). */
  minSelections: number
  /** `max_selections`. El tope cuenta POR ZONA (D5) y lo valida el servidor en las
   *  cuatro rutas de cobro: si la pantalla no lo impone, el 422 le llega al cliente
   *  en el checkout — o peor, el 5º ingrediente "gratis" de un grupo elige-4 nunca
   *  pasa por el grupo de extras pagos. 0 o negativo = sin tope (contrato del
   *  form de admin, auditoría 2026-07-31). */
  maxSelections: number
  options: ZonedOption[]
}

/** Lo que el armador le cuenta al mundo mientras se construye la pizza. Es la
 *  materia prima de la pantalla del cliente: nada de esto existe todavía como
 *  línea de carrito. */
/** Lo que compone el `detail` de un renglón, como DATOS y no como texto: la
 *  pantalla del cliente lo escribe en su idioma. `detail` sigue viajando en
 *  español para un display de otra versión. */
export type ZonedLineDetail = { zone?: Zone; double?: boolean; removal?: boolean }

export type ZonedPreview = {
  layout: ZoneLayout
  toppings: PizzaTopping[]
  /** Título tipo comanda: "Familiar (40 cm) · Delgada · Roja". */
  headline: string
  /** El título en inglés, solo si algún nombre difiere del canónico. */
  headlineEn?: string
  /** Sin tamaño todavía el título es un texto fijo ("Elige un tamaño para
   *  empezar"); esta clave deja que la pantalla del cliente lo diga en su idioma. */
  headlineKey?: "chooseSize"
  lines: Array<{
    label: string
    labelEn?: string
    detail?: string
    detailParts?: ZonedLineDetail
    amount: number
    kind: "size" | "option"
    color?: string
  }>
  total: number
  /** Ingredientes que todavía NO están puestos, del más barato al más caro. */
  suggestions: Array<{ name: string; nameEn?: string; price: number }>
  /** La base: lo que hace que la salsa blanca y el tamaño se VEAN. */
  base: { salsas: Array<{ zone: Zone; tipo: Salsa }>; masa?: Masa; queso?: NivelQueso; escala: number }
}

export interface ZonedBuilderProps {
  itemName: string
  basePrice: number
  groups: readonly ZonedGroup[]
  mode: ZonedPricingMode
  /** Rejilla precio × tamaño del local, indexada con `sizeGridKey`. Vacía es válido:
   *  todo cae al precio plano, igual que en el servidor. */
  sizeGrid: Map<string, SizePriceCell>
  cfg: PricingConfig
  quantity: number
  onQuantityChange: (n: number) => void
  /**
   * `compact` = teléfono: la pizza es una firma de 76px y manda la lista.
   * `kiosk`  = pantalla grande y cliente DE PIE: la pizza es el protagonista, a
   * dos columnas, y todo lo tocable crece. Es una PRESENTACIÓN, no otro
   * componente: duplicar el armador garantizaría que las reglas diverjan.
   */
  variant?: "compact" | "kiosk" 
  /**
   * Se llama en cada cambio con la pizza en curso. Hoy lo usa el POS para
   * espejarla en la pantalla del cliente.
   *
   * ⚠️ Debe venir MEMOIZADA por quien la pasa: se usa como dependencia del efecto
   * que la dispara, así que una función nueva en cada render emitiría sin parar.
   */
  onPreview?: (p: ZonedPreview) => void
  /**
   * El dibujo lo pinta OTRO (hoy el modal del kiosko, a pantalla completa y montado
   * sobre la esquina). Acá se oculta el inline para no mostrar dos pizzas.
   *
   * No se dibuja "afuera" por gusto: el contenedor del armador scrollea, y todo lo
   * que scrollea recorta. Una pizza que se desborda de verdad tiene que vivir fuera
   * de ese contenedor.
   */
  pizzaExterna?: boolean
  onAdd: (r: {
    layout: ZoneLayout
    /** Normalizadas Y con precio: el modal las copia tal cual a la línea del carrito. */
    selections: PricedZonedSelection[]
    modifiersTotal: number
    sizeOptionId: string | null
  }) => void
}



/** La regla del grupo en el shape que valida el motor — el MISMO shape que el
 *  servidor arma en loadZonedQuoteFacts. Mapeo puro: vive fuera del componente. */
function ruleFor(g: ZonedGroup): ZonedGroupRule {
  return {
    groupId: g.id,
    required: g.required,
    minSelections: g.minSelections,
    maxSelections: g.maxSelections,
    zonable: g.zonable,
    isSizeGroup: g.isSize,
  }
}

export function ZonedBuilder({
  itemName, basePrice, groups, mode, sizeGrid, cfg, quantity, onQuantityChange, onAdd, variant = "compact", onPreview, pizzaExterna = false,
}: ZonedBuilderProps) {
  const kiosk = variant === "kiosk"
  // «¡Quedan pocas!» por ingrediente (components/menu/LowStockContext). En el POS
  // no hay proveedor y responde «no»: el armador queda como estaba.
  const lowStock = useLowStock()
  const sizeGroup = useMemo(() => groups.find((g) => g.isSize) ?? null, [groups])
  const choiceGroups = useMemo(() => groups.filter((g) => !g.isSize && g.single), [groups])
  const toppingGroups = useMemo(() => groups.filter((g) => !g.isSize && !g.single), [groups])

  const allOptions = useMemo(() => {
    const m = new Map<string, { o: ZonedOption; g: ZonedGroup }>()
    groups.forEach((g) => g.options.forEach((o) => m.set(o.id, { o, g })))
    return m
  }, [groups])

  // Los agotados quedan FUERA de la siembra aunque sean default: si no, el
  // armador pre-coloca en la pizza un ingrediente que la cocina no tiene, sin
  // que el cliente lo haya tocado.
  const defaultIds = useMemo(() => seedDefaultOptionIds(groups), [groups])

  const [state, dispatch] = useReducer(
    zonedBuildReducer,
    { layout: "whole" as ZoneLayout, defaultOptionIds: defaultIds },
    initZonedBuild,
  )
  const [sizeId, setSizeId] = useState<string | null>(() => pickSizeOptionId(sizeGroup))
  /** Qué ingrediente está desplegado mostrando su zona y su porción. */
  const [openId, setOpenId] = useState<string | null>(null)
  /** Por qué "Agregar" no agregó: el mínimo o el tope de un grupo. Se limpia con
   *  cualquier interacción, igual que el error del modal clásico. */
  const [limitError, setLimitError] = useState<string | null>(null)

  // ── Límites del grupo (min por línea, max POR ZONA — D5) ───────────────────
  //
  // 🔴 NO HAY CONTEO PROPIO. La regla vive en validateZonedGroup —la misma que el
  // servidor aplica en validateZonedLoad— y el estado hipotético se obtiene del
  // MISMO reductor que aplicará el cambio. Reimplementar acá "cuántos caben" es
  // exactamente cómo el armador quedó sin tope la primera vez (corrección
  // 2026-08-17: solo se imponía max_selections = 1).

  const optionIdsByGroup = useMemo(() => {
    const m = new Map<string, Set<string>>()
    groups.forEach((g) => m.set(g.id, new Set(g.options.map((o) => o.id))))
    return m
  }, [groups])

  /** Selecciones que pertenecen al grupo, sobre un estado real o simulado. */
  function groupSelections(st: ZonedBuildState, g: ZonedGroup): ZonedSelection[] {
    const ids = optionIdsByGroup.get(g.id)
    return ids ? st.selections.filter((s) => ids.has(s.optionId)) : []
  }

  /** Estado que quedaría tras despachar estas acciones, sin despacharlas. El
   *  reductor es puro a propósito: simular con él es la única forma de no
   *  duplicar D13 (incluidos que se materializan por zonas) en la validación. */
  function simulate(actions: readonly ZonedBuildAction[]): ZonedBuildState {
    return actions.reduce(zonedBuildReducer, state)
  }

  /** ¿Estas acciones dejarían al grupo PASADO de su tope? El mínimo no bloquea
   *  interacciones (se arma de a poco); solo lo exige el botón de agregar. */
  function wouldExceed(actions: readonly ZonedBuildAction[], g: ZonedGroup): boolean {
    const next = simulate(actions)
    const v = validateZonedGroup(ruleFor(g), groupSelections(next, g), next.layout)
    return !v.ok && v.reason !== "min_per_line"
  }

  /** Despacha en orden y limpia el error de límites: la interacción ya cambió lo
   *  que el mensaje describía. */
  function dispatchAll(actions: readonly ZonedBuildAction[]) {
    setLimitError(null)
    actions.forEach(dispatch)
  }

  /**
   * 🔴 EL PRECIO DE UN INGREDIENTE DEPENDE DEL TAMAÑO ELEGIDO.
   *
   * Un pepperoni cuesta $1.50 en mediana y $3.99 en familiar. Antes esta pantalla
   * mostraba siempre el `price_delta` plano de la opción, mientras el servidor
   * cobraba la celda de la rejilla: en familiar el cliente veía $2.99 y pagaba
   * $3.99 — veía MENOS de lo que se le cobraba.
   *
   * La resolución NO se reimplementa acá: se llama a `resolveBaseCardPrice`, la
   * MISMA función que usa el motor del servidor, con la misma caída a `price_delta`
   * cuando falta la celda (D10). Escribir una segunda versión de esta regla es
   * exactamente cómo nació el desajuste que esto arregla.
   */
  const basePriceOf = useMemo(() => {
    return (optionId: string, flatPrice: number) =>
      resolveBaseCardPrice(optionId, sizeId, { price_delta: flatPrice }, sizeGrid)
  }, [sizeId, sizeGrid])

  /**
   * Selecciones normalizadas Y COTIZADAS — la ÚNICA resolución de precio del armador.
   *
   * El total en vivo de abajo, el resumen y la línea que se manda al carrito salen todos
   * de este mismo arreglo. Antes el precio por fila se calculaba solo para agregarlo en
   * un total y se perdía: el modal escribía `price_delta: 0` en cada topping y el
   * carrito enseñaba la pizza sin extras (lib/menu/__tests__/zonedCartParity.test.ts).
   */
  const normalized = useMemo(
    () =>
      priceZonedBuild(state, (s) => {
        const e = allOptions.get(s.optionId)
        if (!e) return 0
        return resolveZonedUnitPriceCents({
          baseCardPrice: basePriceOf(e.o.id, e.o.basePrice),
          zone: s.zone, mode, cfg, isIncluded: s.isIncluded,
        })
      }),
    [state, allOptions, mode, cfg, basePriceOf],
  )

  const modifiersCents = useMemo(() => sumZonedCents(normalized), [normalized])

  /**
   * El extra del TAMAÑO, cotizado con la MISMA función que usa la caja para su fila.
   *
   * Antes se leía crudo (`o.basePrice`) mientras `quoteZonedLine` lo pasa por
   * `effectiveUnitPrice` como a cualquier otra fila. Con precio dual encendido son el
   * mismo número —por eso no se veía—, pero con dual apagado y base 'cash' el armador
   * enseñaba un tamaño que la caja no cobraba, y su propio total dejaba de coincidir
   * con el de la línea que él mismo manda al carrito.
   * Ver lib/menu/__tests__/zonedSizeParity.test.ts.
   */
  const sizeExtraCents = useMemo(() => {
    const o = sizeGroup?.options.find((x) => x.id === sizeId)
    if (!o) return 0
    return resolveZonedUnitPriceCents({
      baseCardPrice: o.basePrice, zone: "whole", mode, cfg, isIncluded: false,
    })
  }, [sizeGroup, sizeId, mode, cfg])

  const unit = basePrice + (sizeExtraCents + modifiersCents) / 100
  const total = unit * Math.max(1, quantity)

  /** Selecciones vivas de una opción (puede estar en varias zonas). */
  function placements(optionId: string) {
    return state.selections.filter((s) => s.optionId === optionId && !s.isRemoval)
  }
  const isOn = (optionId: string) => placements(optionId).length > 0

  /** Zona donde está hoy. Si está en varias, manda la primera no-whole. */
  function zoneOf(optionId: string): Zone {
    const p = placements(optionId)
    const zoned = p.find((s) => s.zone !== "whole")
    return zoned?.zone ?? p[0]?.zone ?? "whole"
  }
  const qtyOf = (optionId: string) => placements(optionId)[0]?.quantity ?? 1

  // Los handlers construyen su lista de acciones UNA vez y la comparten con la
  // simulación: si la guarda mirara una lista y el dispatch otra, el tope se
  // validaría sobre un cambio distinto del que se aplica.

  /** Acciones que limpian una opción de TODAS sus zonas, unidad por unidad. */
  function clearActions(optionId: string): ZonedBuildAction[] {
    return placements(optionId).flatMap((s) =>
      Array.from({ length: s.quantity }, (): ZonedBuildAction => ({ type: "removeOption", optionId, zone: s.zone }))
    )
  }

  function clearOption(optionId: string) {
    dispatchAll(clearActions(optionId))
  }

  /** El alta siempre entra por 'whole'; la zona se decide después con `moveTo`. */
  function addActions(optionId: string): ZonedBuildAction[] {
    return [{ type: "setActiveZone", zone: "whole" }, { type: "addOption", optionId }]
  }

  /** ¿El toque de alta de esta opción quedaría fuera del tope de su grupo? Es lo
   *  que pinta la opción como bloqueada Y lo que ignora el toque si igual llega. */
  function addBlocked(optionId: string): boolean {
    const entry = allOptions.get(optionId)
    return entry ? wouldExceed(addActions(optionId), entry.g) : false
  }

  /** SELECCIÓN ÚNICA: elegir una opción de un grupo `single` apaga a sus hermanas.
   *  Es el bug que reportó Samuel — se podían marcar "Sin queso", "Normal" y
   *  "Doble" a la vez, y eso llegaba así a la cocina. El reductor no conoce grupos,
   *  así que la exclusividad la impone quien SÍ los conoce: esta pantalla. */
  function pickSingle(group: ZonedGroup, optionId: string) {
    group.options.forEach((o) => { if (o.id !== optionId) clearOption(o.id) })
    if (!isOn(optionId)) dispatchAll(addActions(optionId))
  }

  function toggleTopping(optionId: string) {
    // QUITAR va primero, y a propósito: si el ingrediente se acabó mientras el
    // modal estaba abierto, o venía puesto de un armado restaurado, la única
    // salida del cliente es poder sacarlo. Bloquear el agotado antes de esta
    // línea lo dejaría atrapado con una pizza que no puede pedir ni corregir.
    if (isOn(optionId)) { clearOption(optionId); setOpenId(null); return }
    // Agregar sí se bloquea, mismo criterio que el tope. La UI ya lo pinta
    // apagado; esto garantiza que no entre ni por un render.
    if (allOptions.get(optionId)?.o.soldOut) return
    // Al tope: no-op, igual que toggleMultiple del POS. El botón ya se muestra
    // bloqueado; esto solo garantiza que el estado inválido no exista ni un render.
    if (addBlocked(optionId)) return
    dispatchAll(addActions(optionId))
    setOpenId(optionId)
  }

  /** Acciones de mover un ingrediente de zona: quitarlo de donde está y ponerlo
   *  donde va, conservando su cantidad. */
  function moveActions(optionId: string, zone: Zone): ZonedBuildAction[] {
    const q = qtyOf(optionId)
    const acts: ZonedBuildAction[] = clearActions(optionId)
    if (zone !== "whole" && state.layout === "whole") acts.push({ type: "setLayout", layout: "halves" })
    acts.push({ type: "setActiveZone", zone })
    for (let i = 0; i < q; i++) acts.push({ type: "addOption", optionId })
    return acts
  }

  /** ¿Mover la opción a esa zona rompería el tope? (Mover hacia 'whole' carga
   *  TODAS las zonas a la vez — puede no caber aunque en su mitad cupiera.) */
  function moveBlocked(optionId: string, zone: Zone): boolean {
    const entry = allOptions.get(optionId)
    return entry ? wouldExceed(moveActions(optionId, zone), entry.g) : false
  }

  /** Mover un ingrediente de zona: se quita de donde está y se pone donde va. La
   *  disposición de la línea se INFIERE de esto — el cliente nunca ve la pregunta
   *  "¿cómo la dividimos?". */
  function moveTo(optionId: string, zone: Zone) {
    if (moveBlocked(optionId, zone)) return
    dispatchAll(moveActions(optionId, zone))
  }

  function portionActions(optionId: string, double: boolean): ZonedBuildAction[] {
    const z = zoneOf(optionId)
    const q = qtyOf(optionId)
    const want = double ? 2 : 1
    if (q === want) return []
    const acts: ZonedBuildAction[] = [{ type: "setActiveZone", zone: z }]
    if (want > q) for (let i = q; i < want; i++) acts.push({ type: "addOption", optionId })
    else for (let i = q; i > want; i--) acts.push({ type: "removeOption", optionId, zone: z })
    return acts
  }

  /** ¿Doblar la porción excedería el tope del grupo? (Bajar a Normal nunca.) */
  function portionBlocked(optionId: string, double: boolean): boolean {
    if (!double) return false
    const entry = allOptions.get(optionId)
    const acts = portionActions(optionId, double)
    return entry && acts.length > 0 ? wouldExceed(acts, entry.g) : false
  }

  function setPortion(optionId: string, double: boolean) {
    const acts = portionActions(optionId, double)
    if (acts.length === 0) return
    if (portionBlocked(optionId, double)) return
    dispatchAll(acts)
  }

  /** Centavos que suma esta opción en esa zona, ya con el tamaño resuelto. */
  function priceOf(o: ZonedOption, zone: Zone) {
    return resolveZonedUnitPriceCents({
      baseCardPrice: basePriceOf(o.id, o.basePrice), zone, mode, cfg, isIncluded: o.isDefault,
    })
  }

  /** Requisito del grupo para el encabezado de sección, con estado vivo. El tope
   *  POR ZONA no se numera acá (sería mentira en mitad y mitad): lo comunican las
   *  opciones bloqueadas; el número del encabezado habla del mínimo y del rango. */
  function groupHint(g: ZonedGroup): { text: string; missing: number; satisfied: boolean } | null {
    const rule = ruleFor(g)
    const min = effectiveZonedMin(rule)
    const max = g.maxSelections > 0 ? g.maxSelections : Infinity
    let text: string | null = null
    if (min > 0 && max === min) text = `Elige ${min}`
    else if (min > 0 && Number.isFinite(max)) text = `Elige ${min}–${max}`
    else if (min > 0) text = `Elige al menos ${min}`
    else if (Number.isFinite(max) && max > 1) text = `Hasta ${max}`
    if (!text) return null
    const units = countZonedUnits(groupSelections(state, g))
    return { text, missing: Math.max(0, min - units), satisfied: min > 0 && units >= min }
  }

  /** El cierre exige lo que el servidor va a exigir al cobrar (validateZonedLoad):
   *  el mínimo POR LÍNEA y el tope POR ZONA de cada grupo. Sin esta puerta, la
   *  pizza inválida se descubre con un 422 en el checkout — o en la caja del POS. */
  function handleAdd() {
    for (const g of groups) {
      if (g.isSize) continue
      const v = validateZonedGroup(ruleFor(g), groupSelections(state, g), state.layout)
      if (v.ok) continue
      const min = effectiveZonedMin(ruleFor(g))
      const units = countZonedUnits(groupSelections(state, g))
      const msg =
        v.reason === "min_per_line"
          ? min === 1
            ? `Elige una opción en "${g.name}".`
            : `Elige ${min} en "${g.name}" — llevas ${units}.`
          : v.reason === "max_per_zone" || v.reason === "max_per_line"
            ? `"${g.name}" admite hasta ${g.maxSelections}${v.zone && v.zone !== "whole" ? " por mitad" : ""} — quita alguno.`
            : `Revisa la selección de "${g.name}".`
      setLimitError(msg)
      document.getElementById(`zb-group-${g.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }
    setLimitError(null)
    onAdd({
      layout: state.layout,
      selections: normalized,
      modifiersTotal: modifiersCents / 100,
      sizeOptionId: sizeId,
    })
  }

  /** El resumen anclado arriba. Se arma con el MISMO criterio que la comanda de
   *  cocina: bloques por zona, en orden de armado, removals dentro de su bloque
   *  (§9 del spec). Lo que el cliente confirma es literalmente lo que el pizzero
   *  va a leer — si alguna vez divergen, es un bug de los dos lados a la vez.
   *
   *  Es progresivo a propósito: mientras falta el tamaño lo pide, y después se va
   *  llenando. Un resumen que solo aparece al final no guía a nadie. */
  const resumen = useMemo(() => {
    const sizeName = sizeGroup?.options.find((o) => o.id === sizeId)?.name ?? null
    // Masa, salsa y queso describen la pizza ENTERA: van en el título. Meterlas en
    // los bloques de zona las escondería entre los toppings.
    // Se consulta `state.selections` directo y no el helper `isOn`: llamarlo desde
    // aquí mete una dependencia que el React Compiler no puede inferir y descarta
    // la memoización del componente entero.
    const puesto = new Set(
      state.selections.filter((x) => !x.isRemoval).map((x) => x.optionId),
    )
    const bases: string[] = []
    const basesEn: string[] = []
    let algunaEn = false
    choiceGroups.forEach((g) => {
      const sel = g.options.find((o) => puesto.has(o.id))
      if (sel) {
        bases.push(sel.name)
        basesEn.push(sel.nameEn ?? sel.name)
        if (sel.nameEn) algunaEn = true
      }
    })
    const titulo = sizeName ? [sizeName, ...bases].join(" · ") : "Elige un tamaño para empezar"
    // El mismo título con los nombres en inglés, solo si alguno difiere: la
    // pantalla del cliente lo usa cuando está en inglés (spec §4.3, mismo
    // contrato que `nameEn` en el carrito).
    const sizeEn = sizeGroup?.options.find((o) => o.id === sizeId)?.nameEn
    const tituloEn =
      sizeName && (sizeEn || algunaEn) ? [sizeEn ?? sizeName, ...basesEn].join(" · ") : undefined

    const ORDEN: Zone[] = ["whole", "left", "right", "q1", "q2", "q3", "q4"]
    const porZona = new Map<Zone, string[]>()
    for (const sel of normalized) {
      const e = allOptions.get(sel.optionId)
      if (!e || e.g.isSize || e.g.single) continue
      const linea = sel.isRemoval
        ? `sin ${e.o.name.toLowerCase()}`
        : `${sel.quantity > 1 ? `${sel.quantity}× ` : ""}${e.o.name}`
      porZona.set(sel.zone, [...(porZona.get(sel.zone) ?? []), linea])
    }
    const bloques = ORDEN.filter((z) => porZona.has(z)).map((z) => ({
      zona: z === "whole" ? "Entera" : ZONE_SHORT[z],
      texto: (porZona.get(z) ?? []).join(", "),
    }))

    return {
      titulo,
      tituloEn,
      sinTamano: !sizeName,
      bloques,
      vacio: sizeName ? "Sin ingredientes todavía" : "Después eliges masa, salsa e ingredientes",
    }
  }, [sizeGroup, sizeId, choiceGroups, normalized, allOptions, state.selections])

  /** Lo que el dibujo necesita: solo lo que se VE sobre la pizza. El tamaño no es un
   *  ingrediente y las remociones son ausencias, no piezas. */
  const pizzaToppings: PizzaTopping[] = useMemo(
    () =>
      normalized
        .filter((s) => !s.isRemoval)
        .map((s) => {
          const e = allOptions.get(s.optionId)
          // 🔴 SOLO los grupos de INGREDIENTES generan piezas. Los de selección única
          // —tamaño, masa, salsa, queso— describen la pizza entera, no cosas que se
          // ponen encima. Sin este filtro, una Margherita (queso y salsa, nada más)
          // salía cubierta de confeti: "Normal", "Delgada" y "Roja" se dibujaban como
          // toppings, y "Salsa de tomate" encima caía en el patrón del tomate y se
          // pintaba como rodajas. Esas decisiones ahora van a la BASE del dibujo.
          if (!e || e.g.isSize || e.g.single) return null
          return { optionId: s.optionId, name: e.o.name, color: e.o.color, zone: s.zone, quantity: s.quantity }
        })
        .filter((x): x is PizzaTopping => x !== null),
    [normalized, allOptions],
  )

  /**
   * La BASE: salsa (por zona), masa, nivel de queso y tamaño.
   *
   * Sale de los grupos de selección única, que son justo los que dejaron de dibujarse
   * como piezas. Elegir salsa blanca ahora se VE, y una familiar se ve más grande que
   * una mediana — que es lo que el cliente está comprobando del otro lado del
   * mostrador.
   */
  const base = useMemo(() => {
    const salsas: Array<{ zone: Zone; tipo: Salsa }> = []
    let masa: Masa | null = null
    let queso: NivelQueso | null = null
    for (const sel of state.selections) {
      if (sel.isRemoval) continue
      const e = allOptions.get(sel.optionId)
      if (!e || !e.g.single || e.g.isSize) continue
      const sa = salsaDe(e.o.name)
      if (sa) { salsas.push({ zone: sel.zone, tipo: sa }); continue }
      const m = masaDe(e.o.name)
      if (m) { masa = m; continue }
      const q = nivelQuesoDe(e.o.name)
      if (q) queso = q
    }
    const opciones = sizeGroup?.options ?? []
    const i = opciones.findIndex((o) => o.id === sizeId)
    return {
      salsas,
      masa: masa ?? undefined,
      queso: queso ?? undefined,
      escala: i >= 0 ? escalaDeTamano(i, opciones.length) : 1,
    }
  }, [state.selections, allOptions, sizeGroup, sizeId])

  // ── Vista previa para la pantalla del cliente ──────────────────────────────
  //
  // Se arma acá y no en el POS porque acá está TODO: el precio ya resuelto por
  // tamaño, las zonas normalizadas y el orden de decisión. Recalcularlo del otro
  // lado sería una segunda verdad sobre la misma pizza.
  const preview: ZonedPreview = useMemo(() => {
    const puestos = new Set(state.selections.filter((x) => !x.isRemoval).map((x) => x.optionId))

    const lines: ZonedPreview["lines"] = []
    const size = sizeGroup?.options.find((o) => o.id === sizeId)
    if (size) {
      lines.push({
        label: size.name,
        labelEn: size.nameEn,
        amount: basePrice + effectiveUnitPrice(size.basePrice, cfg),
        kind: "size",
      })
    }

    // Un renglón por decisión, en el orden en que se tomó. El cliente sigue su
    // propio pedido armándose; un total que salta solo no explica nada.
    for (const sel of normalized) {
      const e = allOptions.get(sel.optionId)
      if (!e || e.g.isSize) continue
      // Las elecciones únicas GRATIS ya están en el título ("Mediana · Normal ·
      // Delgada · Roja"): repetirlas como renglón llenaba la lista de ruido y
      // empujaba fuera de vista a los ingredientes, que es lo que el cliente
      // revisa. Las que SÍ cuestan se quedan — si no, el desglose dejaría de
      // explicar de dónde sale el total.
      // Se resuelve inline y no con el helper `priceOf`: ese cambia en cada render
      // y arrastraría este memo —y con él, el evento que va al canal del display.
      const centavos = sel.isRemoval
        ? 0
        : resolveZonedUnitPriceCents({
            baseCardPrice: basePriceOf(e.o.id, e.o.basePrice),
            zone: sel.zone, mode, cfg, isIncluded: e.o.isDefault,
          }) * sel.quantity
      if (e.g.single && centavos <= 0) continue
      // Se llama a `resolveZonedUnitPriceCents` directo en vez de al helper
      // `priceOf`: ese helper es una función suelta del cuerpo del componente, así
      // que cambia en cada render y arrastraría este memo con él — y con el memo,
      // el efecto que emite al canal. Un evento de realtime por render es
      // exactamente cómo el poll de 5s del POS se comió la cuota de Sentry.
      const cents = centavos
      const detalle = [
        sel.zone === "whole" ? null : ZONE_SHORT[sel.zone],
        sel.quantity > 1 ? "doble" : null,
        sel.isRemoval ? "sin" : null,
      ].filter(Boolean).join(" · ")
      // Lo mismo como datos, para que la pantalla del cliente lo escriba en su
      // idioma. Ausente cuando no hay nada que decir, igual que `detail`.
      const partes: ZonedLineDetail = {}
      if (sel.zone !== "whole") partes.zone = sel.zone
      if (sel.quantity > 1) partes.double = true
      if (sel.isRemoval) partes.removal = true
      lines.push({
        label: e.o.name,
        labelEn: e.o.nameEn,
        detail: detalle || undefined,
        detailParts: detalle ? partes : undefined,
        amount: cents / 100,
        kind: "option",
        color: e.o.color,
      })
    }

    // Sugerencias: lo que NO está puesto, de menor a mayor precio. El hueco visual
    // en la pizza hace la venta; esto solo le pone nombre y cuánto cuesta.
    const suggestions = toppingGroups
      .flatMap((g) => g.options)
      // Sugerir lo que no hay sería vender un hueco que no se puede llenar.
      .filter((o) => !puestos.has(o.id) && !o.isDefault && !o.soldOut)
      .map((o) => ({ name: o.name, nameEn: o.nameEn, price: effectiveUnitPrice(basePriceOf(o.id, o.basePrice), cfg) }))
      .sort((a, b) => a.price - b.price)
      .slice(0, 4)

    return {
      layout: state.layout,
      toppings: pizzaToppings,
      headline: resumen.titulo,
      headlineEn: resumen.tituloEn,
      headlineKey: resumen.sinTamano ? "chooseSize" : undefined,
      lines,
      total,
      suggestions,
      base,
    }
  }, [state, normalized, allOptions, sizeGroup, sizeId, basePrice, pizzaToppings, resumen, total, toppingGroups, basePriceOf, mode, cfg, base])

  // El efecto depende de `onPreview`, así que quien la pase DEBE memoizarla — está
  // avisado en el tipo. Sin eso, cada render del POS emitiría al canal.
  useEffect(() => {
    onPreview?.(preview)
  }, [onPreview, preview])

  // En kiosko todo crece: el cliente está DE PIE, a medio metro, y toca con el dedo.
  // Apple y Google piden 44px de área táctil; acá se va a ~60 porque no hay teclado
  // ni segunda oportunidad — un toque errado en un kiosko es un cliente frustrado.
  const secTitle = kiosk
    ? "text-[15px] font-bold uppercase tracking-[0.12em] text-[var(--redy-secondary)] mb-4"
    : "text-[12px] font-bold uppercase tracking-[0.12em] text-[var(--redy-muted)] mb-2.5"

  /** Encabezado de sección con el requisito del grupo a la derecha — el mismo
   *  patrón del "Elige hasta N" del modal del POS, con el faltante en vivo. */
  const seccionHeader = (g: ZonedGroup) => {
    const h = groupHint(g)
    return (
      <div className="flex items-baseline justify-between gap-3">
        <h3 className={secTitle}>{g.name}</h3>
        {h ? (
          <span
            className={kiosk ? "text-[13px] font-bold whitespace-nowrap" : "text-[11px] font-bold whitespace-nowrap"}
            style={{ color: h.missing > 0 ? "var(--redy-red)" : "var(--redy-muted)" }}
          >
            {h.text}
            {h.missing > 0 ? ` · falta${h.missing > 1 ? "n" : ""} ${h.missing}` : h.satisfied ? " ✓" : ""}
          </span>
        ) : null}
      </div>
    )
  }

  return (
    <div
      className={
        kiosk
          // 🔴 En VERTICAL (que es como se usa el kiosko casi siempre) no hay dos
          // columnas: es una sola, y la pizza tiene que quedar ANCLADA arriba o
          // deja de tener función — se va con el scroll justo cuando el cliente
          // empieza a elegir, que es cuando más la necesita.
          ? "flex flex-col gap-6 lg:grid lg:grid-cols-[minmax(380px,44%)_1fr] lg:gap-8"
          : "flex flex-col gap-7"
      }
    >
      {/* Envoltura de la columna izquierda. En vertical desaparece (`contents`)
          para que el resumen sea hijo directo del flex y el `sticky` tenga toda
          la columna por dónde viajar. En horizontal se vuelve un ítem de grid
          que se estira a lo alto de la fila — sin ese estiramiento el área del
          ítem mide lo que mide su contenido y el `sticky` no se mueve nunca.
          Ese, y no el breakpoint, era el motivo real de que no se anclara. */}
      <div className={kiosk ? "contents lg:block lg:h-full" : "contents"}>
        {/* ── RESUMEN VIVO, ANCLADO ──────────────────────────────────────────
            No es decoración: es la orden en curso. Acompaña toda la construcción
            y se lee con el formato de la comanda, así el cliente sabe en todo
            momento qué está pidiendo — y confirma lo que va a recibir. */}
        <div
          className={
            kiosk
              // Vertical: fila compacta (pizza al lado del texto) para no comerse
              // la pantalla. Horizontal: columna alta, la pizza de protagonista.
              // 🔴 LA PIZZA ES LA PROTAGONISTA y va anclada ARRIBA A LA IZQUIERDA,
              // no centrada: en un kiosko el cliente entra mirando esa esquina, y es
              // lo primero que tiene que ver crecer. El texto se alinea a la
              // izquierda debajo, no centrado — centrarlo la volvía una tarjeta
              // decorativa en vez del centro de la pantalla.
              // 🔴 LA PIZZA ES LA PROTAGONISTA, arriba a la izquierda.
              // Vertical: fila — pizza gigante a la izquierda, nombre y datos al
              // lado. Horizontal: columna, la pizza ocupa todo el ancho arriba.
              // Centrarla la volvía una tarjeta decorativa en vez del centro de la
              // pantalla; en un kiosko el cliente entra mirando esa esquina.
              // 🔴 LA PIZZA SE DESBORDA DEL RECUADRO, montada sobre su esquina
              // superior izquierda. Que "quepa" prolija adentro la volvía una
              // ilustración de catálogo; que se salga la vuelve el objeto que el
              // cliente está armando. `overflow-visible` es lo que lo permite —
              // sin eso el recuadro le corta el borde.
              ? [
                  "sticky top-0 z-[5] rounded-3xl overflow-visible px-5 py-5 flex items-start gap-4 text-left",
                  "lg:top-4 lg:flex-col lg:items-start lg:px-7 lg:py-7 lg:gap-5",
                  // Con la pizza afuera NO se reserva hueco: va EN FLUJO arriba del
                  // todo (el hero del modal). Reservarlo dejaba un vacío blanco
                  // enorme entre la pizza y el resumen — se veía en vertical, que es
                  // como se usa el kiosko.
                ].join(" ")
              : "sticky top-0 z-[5] -mx-1 px-3 py-2.5 rounded-2xl flex items-start gap-3"
          }
          style={{ background: "#17130F", boxShadow: "0 10px 24px -16px rgba(0,0,0,.9)" }}
        >
          {pizzaExterna ? null : (
          <PizzaCanvas
            toppings={pizzaToppings}
            layout={state.layout}
            // En kiosko los ingredientes CAEN: ahí el cliente arma y mira, y la
            // construcción es parte de lo que compra. En el teléfono y en el POS va
            // apagado — el armador del cajero es una referencia que se consulta a
            // toda velocidad, y algo que salta en cada toque estorba.
            animate={kiosk}
            salsas={base.salsas}
            masa={base.masa}
            queso={base.queso}
            escala={base.escala}
            idPrefix="zb"
            className={
              kiosk
                // Vertical: 150→190 px, ya bien visible sin comerse la lista.
                // Horizontal: hasta 440 px — es EL objeto de la pantalla.
                // Vertical: 200→240 px. Horizontal: hasta 520 px, todo el ancho de
                // la columna. Es EL objeto de la pantalla, no una miniatura.
                // Vertical: proporción de la fila (38%), no un ancho fijo — así la
                // pizza llena el alto del hero en vez de dejar un hueco oscuro
                // debajo. Horizontal: todo el ancho de la columna, hasta 520 px.
                // Márgenes NEGATIVOS en vez de posición absoluta: la pizza se sale
                // del recuadro pero sigue mandando en el alto del bloque, así que
                // nunca se monta sobre los ingredientes de abajo. Con `absolute` el
                // contenedor colapsaría y la pizza taparía los controles.
                ? "flex-none w-[46%] min-w-[190px] max-w-[340px] h-auto -ml-9 -mt-10 -mb-4 drop-shadow-2xl lg:w-[118%] lg:max-w-none lg:-ml-16 lg:-mt-16 lg:-mr-10 lg:-mb-2"
                : "flex-none w-[76px] sm:w-[92px] h-auto"
            }
            title={`Vista de ${itemName}`}
          />
          )}

          <div className={kiosk ? "flex-1 min-w-0 lg:w-full" : "flex-1 min-w-0"}>
            {/* EL NOMBRE DEL PLATO. Hasta ahora no aparecía en ningún lado del
                armador: el encabezado mostraba el tamaño y la base, así que un
                cliente que llegaba al kiosko y tocaba "Suprema" perdía de vista qué
                estaba armando en cuanto se abría la pantalla. */}
            {/* El nombre solo cuando el armador es el dueño de la cabecera. Con la
                pizza afuera, el modal ya lo muestra arriba y repetirlo llenaba la
                pantalla de la misma palabra dos veces. */}
            {kiosk && !pizzaExterna ? (
              <h2
                className="text-[26px] lg:text-[38px] font-black leading-[1.02] tracking-[-0.03em]"
                style={{ color: "#F5F0EC" }}
              >
                {itemName}
              </h2>
            ) : null}
            <p
              className={
                kiosk
                  ? "text-[13px] lg:text-[16px] font-semibold leading-snug mt-1.5"
                  : "text-[13.5px] font-bold leading-tight"
              }
              style={{ color: kiosk ? "#B9ADA6" : "#F5F0EC" }}
            >
              {resumen.titulo}
            </p>

            {/* En KIOSKO, el mismo desglose que ve el cliente en el mostrador:
                un renglón por decisión, con el ícono del ingrediente —la misma forma
                y el mismo color que su pieza sobre la pizza— y su precio. El pedido
                se lee mientras se arma, no al final.

                En teléfono se queda el resumen por zonas: ahí no hay ancho para
                tarjetas, y apretarlas las volvería ilegibles. */}
            {kiosk && preview.lines.length > 0 ? (
              <>
              {/* ── VERTICAL: TIRA COMPACTA DE ALTO FIJO ─────────────────────────
                  Reportado por Samuel: cada artículo agregaba un renglón al resumen
                  y la tarjeta crecía comiéndose la navegación — con 6 ingredientes
                  ya no se veían los controles. Y esa lista REPITE lo que la pizza
                  dibujada ya muestra.

                  En vertical el resumen pasa a ser una tira de fichas en UNA fila
                  con scroll horizontal (oculto): cada ingrediente es su glifo + su
                  nombre, los pagos muestran el precio. Diez ingredientes ocupan lo
                  mismo que dos. El detalle por zona vive en la pizza y en la
                  tarjeta de cada ingrediente, no acá. */}
              <div
                className="mt-3 flex lg:hidden items-center gap-2 overflow-x-auto pb-1"
                style={{ scrollbarWidth: "none" }}
              >
                {preview.lines.filter((l) => l.kind !== "size").map((l, i) => (
                  <span
                    key={`${l.label}-${i}`}
                    className="flex-none inline-flex items-center gap-1.5 rounded-full pl-1.5 pr-3 py-1.5"
                    style={{ background: "rgba(255,255,255,.07)" }}
                  >
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ background: "rgba(0,0,0,.3)" }}
                    >
                      <IngredientGlyph name={l.label} color={l.color ?? "#F2D479"} size={17} />
                    </span>
                    <span className="text-[13px] font-semibold whitespace-nowrap" style={{ color: "#F5F0EC" }}>
                      {l.label}
                    </span>
                    {l.amount > 0 ? (
                      <span className="text-[12px] font-bold whitespace-nowrap" style={{ color: "#F0904F", fontVariantNumeric: "tabular-nums" }}>
                        +${l.amount.toFixed(2)}
                      </span>
                    ) : null}
                  </span>
                ))}
              </div>

              {/* ── HORIZONTAL: la lista completa — ahí el resumen tiene columna
                  propia y el espacio no compite con los controles. */}
              <div className="mt-4 hidden lg:flex flex-col gap-1.5 pb-1" style={{ scrollbarWidth: "none" }}>
                {preview.lines.map((l, i) => (
                  <div
                    key={`${l.label}-${l.detail ?? ""}-${i}`}
                    className="flex items-center gap-2.5 rounded-xl px-3 py-2"
                    style={{ background: "rgba(255,255,255,.06)" }}
                  >
                    <span
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-none"
                      style={{ background: "rgba(0,0,0,.28)" }}
                    >
                      {l.kind === "size"
                        ? <span className="w-2 h-2 rounded-full" style={{ background: "#8E827C" }} />
                        : <IngredientGlyph name={l.label} color={l.color ?? "#F2D479"} size={18} />}
                    </span>
                    <span className="text-[14px] font-semibold truncate" style={{ color: "#F5F0EC" }}>{l.label}</span>
                    {l.detail ? (
                      <span
                        className="text-[11px] font-bold uppercase tracking-[0.08em] rounded px-1.5 py-0.5 flex-none"
                        style={{ background: "rgba(242,100,48,.18)", color: "#F0904F" }}
                      >
                        {l.detail}
                      </span>
                    ) : null}
                    <span className="ml-auto text-[13px] font-bold flex-none" style={{ color: l.amount > 0 ? "#F5F0EC" : "#8E827C", fontVariantNumeric: "tabular-nums" }}>
                      {l.amount > 0 ? `$${l.amount.toFixed(2)}` : "incluido"}
                    </span>
                  </div>
                ))}
              </div>
              </>
            ) : resumen.bloques.length > 0 ? (
              <div className="mt-1.5 flex flex-col gap-[3px]">
                {resumen.bloques.map((b) => (
                  <p key={b.zona} className={kiosk ? "text-[12px] lg:text-[13px] leading-snug flex gap-2" : "text-[11.5px] leading-snug flex gap-2"}>
                    <span className="flex-none font-bold uppercase tracking-[0.08em]" style={{ color: "#F26430", minWidth: 58 }}>{b.zona}</span>
                    <span className="min-w-0" style={{ color: "#C4B8B2" }}>{b.texto}</span>
                  </p>
                ))}
              </div>
            ) : (
              <p className={kiosk ? "text-[12px] lg:text-[13px] mt-1" : "text-[11.5px] mt-1"} style={{ color: "#8E827C" }}>{resumen.vacio}</p>
            )}

            <p className={kiosk ? "text-[30px] lg:text-[44px] font-black mt-3 lg:mt-5 tracking-[-0.03em] leading-none" : "text-[16px] font-extrabold mt-2 tracking-[-0.02em]"} style={{ color: "#F5F0EC", fontVariantNumeric: "tabular-nums" }}>
              ${total.toFixed(2)}
              {quantity > 1 ? <span className="text-[11.5px] font-semibold ml-1.5" style={{ color: "#8E827C" }}>· {quantity} pizzas</span> : null}
            </p>
          </div>
        </div>
      </div>

      <div className={kiosk ? "flex flex-col gap-8" : "contents"}>

      {/* 1 · TAMAÑO — primero porque gobierna el precio de todo lo demás. */}
      {sizeGroup ? (
        <section>
          <h3 className={secTitle}>Tamaño</h3>
          <div className="grid grid-cols-3 gap-2">
            {sizeGroup.options.map((o) => {
              const agotado = o.soldOut === true
              return (
              <button
                key={o.id}
                type="button"
                onClick={() => { if (!agotado) setSizeId(o.id) }}
                aria-pressed={sizeId === o.id}
                aria-disabled={agotado}
                className={kiosk ? "rounded-2xl border-2 px-5 py-5 text-left transition-colors" : "rounded-xl border px-3 py-2.5 text-left transition-colors"}
                style={{
                  borderColor: sizeId === o.id ? "var(--redy-red)" : "var(--redy-border)",
                  background: sizeId === o.id ? "color-mix(in srgb, var(--redy-red) 10%, transparent)" : "transparent",
                  opacity: agotado ? 0.45 : undefined,
                }}
              >
                <span className={kiosk ? "block text-[17px] font-bold leading-tight" : "block text-[13.5px] font-semibold leading-tight"}>{o.name}</span>
                <span className={kiosk ? "block text-[16px] font-semibold text-[var(--redy-secondary)] mt-1.5" : "block text-[12px] text-[var(--redy-muted)] mt-1"} style={{ fontVariantNumeric: "tabular-nums" }}>
                  {agotado ? "Agotado" : `$${(basePrice + effectiveUnitPrice(o.basePrice, cfg)).toFixed(2)}`}
                </span>
              </button>
              )
            })}
          </div>
        </section>
      ) : null}

      {/* 2 · CANTIDAD */}
      <section>
        <h3 className={secTitle}>Cantidad</h3>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
            className={kiosk ? "w-16 h-16 rounded-full border-2 text-[26px] font-bold disabled:opacity-40" : "w-10 h-10 rounded-full border text-[18px] font-bold disabled:opacity-40"}
            style={{ borderColor: "var(--redy-border)" }}
            disabled={quantity <= 1}
            aria-label="Quitar una"
          >−</button>
          <span className={kiosk ? "text-[26px] font-extrabold w-12 text-center" : "text-[17px] font-bold w-6 text-center"} style={{ fontVariantNumeric: "tabular-nums" }}>{quantity}</span>
          <button
            type="button"
            onClick={() => onQuantityChange(quantity + 1)}
            className={kiosk ? "w-16 h-16 rounded-full border-2 text-[26px] font-bold" : "w-10 h-10 rounded-full border text-[18px] font-bold"}
            style={{ borderColor: "var(--redy-border)" }}
            aria-label="Agregar una"
          >+</button>
        </div>
      </section>

      {/* 3 · GRUPOS DE UNA SOLA OPCIÓN — masa, salsa, queso. Radio de verdad. */}
      {choiceGroups.map((g) => (
        <section key={g.id} id={`zb-group-${g.id}`}>
          {seccionHeader(g)}
          <div className="grid grid-cols-2 gap-2">
            {g.options.map((o) => {
              const on = isOn(o.id)
              const cents = priceOf(o, zoneOf(o.id))
              return (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => pickSingle(g, o.id)}
                  className={kiosk ? "rounded-2xl border-2 px-5 py-4 flex items-center gap-3.5 text-left transition-colors" : "rounded-xl border px-3 py-2.5 flex items-center gap-2.5 text-left transition-colors"}
                  style={{
                    borderColor: on ? "var(--redy-red)" : "var(--redy-border)",
                    background: on ? "color-mix(in srgb, var(--redy-red) 9%, transparent)" : "transparent",
                  }}
                >
                  <span
                    className={kiosk ? "shrink-0 w-[26px] h-[26px] rounded-full border-2 flex items-center justify-center" : "shrink-0 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center"}
                    style={{ borderColor: on ? "var(--redy-red)" : "var(--redy-border)" }}
                  >
                    {on ? <span className={kiosk ? "w-[14px] h-[14px] rounded-full" : "w-[9px] h-[9px] rounded-full"} style={{ background: "var(--redy-red)" }} /> : null}
                  </span>
                  <span className="min-w-0">
                    <span className={kiosk ? "block text-[16px] font-bold truncate" : "block text-[13.5px] font-semibold truncate"}>{o.name}</span>
                    {cents > 0 ? (
                      <span className="block text-[11.5px] text-[var(--redy-muted)]" style={{ fontVariantNumeric: "tabular-nums" }}>
                        +${(cents / 100).toFixed(2)}
                      </span>
                    ) : null}
                  </span>
                </button>
              )
            })}
          </div>
        </section>
      ))}

      {/* 4 · INGREDIENTES — la zona y la porción viven DENTRO de cada uno. */}
      {toppingGroups.map((g) => (
        <section key={g.id} id={`zb-group-${g.id}`}>
          {seccionHeader(g)}
          <div className="flex flex-col gap-2">
            {g.options.map((o) => {
              const on = isOn(o.id)
              const open = openId === o.id && on
              const z = zoneOf(o.id)
              const cents = priceOf(o, z)
              // Al tope del grupo, las opciones sin marcar se ven y se comportan
              // bloqueadas. Es lo que empuja el 5º ingrediente hacia el grupo de
              // extras PAGOS de abajo, en vez de regalarlo en el grupo lleno.
              const agotada = o.soldOut === true
              const bloqueada = !on && (agotada || addBlocked(o.id))
              return (
                <div
                  key={o.id}
                  className="rounded-xl border overflow-hidden transition-colors"
                  style={{
                    borderColor: on ? "var(--redy-red)" : "var(--redy-border)",
                    background: on ? "color-mix(in srgb, var(--redy-red) 7%, transparent)" : "transparent",
                    opacity: bloqueada ? 0.45 : undefined,
                  }}
                >
                  <button
                    type="button"
                    aria-pressed={on}
                    aria-disabled={bloqueada}
                    onClick={() => {
                      // Si YA está puesto, el panel se abre aunque esté agotado:
                      // ahí vive el botón de quitarlo.
                      if (on) setOpenId(open ? null : o.id)
                      else if (!agotada) toggleTopping(o.id)
                    }}
                    className={kiosk ? "w-full px-5 py-4 flex items-center gap-4 text-left" : "w-full px-3 py-2.5 flex items-center gap-2.5 text-left"}
                  >
                    {/* La forma REAL del ingrediente, no un punto de color: el mismo
                        dibujo que va a caer sobre la pizza. En kiosko se luce. */}
                    <IngredientGlyph name={o.name} color={o.color} size={kiosk ? 40 : 20} />
                    <span className="flex-1 min-w-0">
                      <span className={kiosk ? "block text-[17px] font-bold truncate" : "block text-[13.5px] font-semibold truncate"}>{o.name}</span>
                      {!agotada && lowStock.option(o.id) && (
                        <span className="block mt-1"><LowStockBadge size="sm" /></span>
                      )}
                      <span className={kiosk ? "block text-[14px] text-[var(--redy-secondary)] mt-0.5" : "block text-[11.5px] text-[var(--redy-muted)]"} style={{ fontVariantNumeric: "tabular-nums" }}>
                        {on
                          ? `${ZONE_SHORT[z]}${qtyOf(o.id) > 1 ? " · doble" : ""}${cents > 0 ? ` · +$${((cents * qtyOf(o.id)) / 100).toFixed(2)}` : ""}`
                          : agotada ? "Agotado" : bloqueada ? "Tope alcanzado" : o.isDefault ? "Incluido" : cents > 0 ? `+$${(cents / 100).toFixed(2)}` : "Sin costo"}
                      </span>
                    </span>
                    <span className="shrink-0 text-[18px] leading-none" style={{ color: on ? "var(--redy-red)" : "var(--redy-muted)" }}>
                      {on ? (open ? "×" : "✓") : "+"}
                    </span>
                  </button>

                  {open ? (
                    <div className="px-3 pb-3 flex flex-col gap-2">
                      {g.zonable ? (
                        <div className="grid grid-cols-3 gap-1.5">
                          {ZONES.map((zz) => {
                            // Mover hacia una zona que quedaría pasada del tope se
                            // bloquea acá mismo: mover a 'Toda' carga las dos
                            // mitades a la vez y puede no caber aunque en su mitad
                            // cupiera.
                            const zonaBloq = z !== zz && moveBlocked(o.id, zz)
                            return (
                              <button
                                key={zz}
                                type="button"
                                disabled={zonaBloq}
                                onClick={() => moveTo(o.id, zz)}
                                aria-pressed={z === zz}
                                className={kiosk ? "rounded-xl py-3.5 text-[15px] font-bold border-2 transition-colors disabled:opacity-40" : "rounded-lg py-1.5 text-[12px] font-semibold border transition-colors disabled:opacity-40"}
                                style={{
                                  borderColor: z === zz ? "var(--redy-red)" : "var(--redy-border)",
                                  background: z === zz ? "var(--redy-red)" : "transparent",
                                  color: z === zz ? "#fff" : "var(--redy-secondary)",
                                }}
                              >
                                {ZONE_SHORT[zz]}
                              </button>
                            )
                          })}
                        </div>
                      ) : null}
                      <div className="grid grid-cols-2 gap-1.5">
                        {[false, true].map((dbl) => {
                          const dblBloq = dbl && portionBlocked(o.id, true)
                          return (
                            <button
                              key={String(dbl)}
                              type="button"
                              disabled={dblBloq}
                              onClick={() => setPortion(o.id, dbl)}
                              aria-pressed={(qtyOf(o.id) > 1) === dbl}
                              className={kiosk ? "rounded-xl py-3.5 text-[15px] font-bold border-2 transition-colors disabled:opacity-40" : "rounded-lg py-1.5 text-[12px] font-semibold border transition-colors disabled:opacity-40"}
                              style={{
                                borderColor: (qtyOf(o.id) > 1) === dbl ? "var(--redy-red)" : "var(--redy-border)",
                                background: (qtyOf(o.id) > 1) === dbl ? "var(--redy-red)" : "transparent",
                                color: (qtyOf(o.id) > 1) === dbl ? "#fff" : "var(--redy-secondary)",
                              }}
                            >
                              {dbl ? "Doble" : "Normal"}
                            </button>
                          )
                        })}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleTopping(o.id)}
                        className="text-[12px] font-semibold text-left"
                        style={{ color: "var(--redy-red)" }}
                      >
                        Quitar de la pizza
                      </button>
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </section>
      ))}

      </div>

      {/* Cierre */}
      <div className="sticky bottom-0 -mx-1 px-1 pt-3 pb-1 border-t"
           style={{ borderColor: "var(--redy-border)", background: "var(--redy-surface, #fff)" }}>
        {limitError ? (
          <p role="alert" className={kiosk ? "mb-2.5 text-[15px] font-bold" : "mb-2 text-[12.5px] font-semibold"} style={{ color: "var(--redy-error)" }}>
            {limitError}
          </p>
        ) : null}
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <span className="block text-[11px] uppercase tracking-[0.13em] text-[var(--redy-muted)]">Total</span>
            <span className="block text-[21px] font-extrabold tracking-[-0.03em]" style={{ fontVariantNumeric: "tabular-nums" }}>
              ${total.toFixed(2)}
            </span>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            className={kiosk ? "rounded-2xl px-10 py-6 text-[20px] font-extrabold text-white" : "rounded-xl px-6 py-3.5 text-[15px] font-bold text-white"}
            style={{ background: "var(--redy-gradient)" }}
          >
            Agregar
          </button>
        </div>
      </div>
    </div>
  )
}
